import { 
  Alert, 
  Box, 
  Button, 
  CircularProgress, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  Typography, 
  Divider, 
  Paper, 
  alpha,
  useTheme
} from '@mui/material';
import React from 'react';
import { ValidationResult, ValidationResultType } from '@common/hooks/useDeploymentValidation';
import { AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';

interface EnhancedActivationDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    isDeployment?: boolean;
    loading?: boolean;
    validationResult: ValidationResult;
}

export const EnhancedActivationDialog: React.FC<EnhancedActivationDialogProps> = ({
    open,
    onClose,
    onConfirm,
    isDeployment = false,
    loading = false,
    validationResult
}) => {
    const { t } = useTranslation(namespace);
    const theme = useTheme();
    const action = isDeployment ? t('chatbot.actionDeploy') : t('chatbot.actionActivate');
    const buttonText = isDeployment ? t('chatbot.deploy') : t('chatbot.activate');

    const renderIssueContent = (issueType: ValidationResultType, index: number) => {
        switch (issueType) {
            case 'EMPTY_FLOW':
                return (
                    <Paper 
                        key={`issue-${index}`} 
                        elevation={0} 
                        sx={{ 
                            mb: 3, 
                            p: 2.5, 
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                            backgroundColor: alpha(theme.palette.error.main, 0.05)
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                            <AlertCircle 
                                size={20} 
                                color={theme.palette.error.main} 
                                style={{ marginRight: theme.spacing(1.5), marginTop: '2px' }} 
                            />
                            <Typography variant="subtitle1" fontWeight={600} color="error.main">
                                {t('chatbot.validation.emptyFlow.title')}
                            </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ ml: 4 }}>
                            {t('chatbot.validation.emptyFlow.description')}
                        </Typography>
                    </Paper>
                );

            case 'NO_TRIGGER':
                return (
                    <Paper 
                        key={`issue-${index}`} 
                        elevation={0} 
                        sx={{ 
                            mb: 3, 
                            p: 2.5, 
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                            backgroundColor: alpha(theme.palette.warning.main, 0.05)
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                            <AlertTriangle 
                                size={20} 
                                color={theme.palette.warning.main} 
                                style={{ marginRight: theme.spacing(1.5), marginTop: '2px' }} 
                            />
                            <Typography variant="subtitle1" fontWeight={600} color="warning.main">
                                {t('chatbot.validation.noTrigger.title')}
                            </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ ml: 4 }}>
                            {t('chatbot.validation.noTrigger.description')}
                        </Typography>
                    </Paper>
                );

            case 'INACTIVE_TRIGGER':
                return (
                    <Paper 
                        key={`issue-${index}`} 
                        elevation={0} 
                        sx={{ 
                            mb: 3, 
                            p: 2.5, 
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                            backgroundColor: alpha(theme.palette.warning.main, 0.05)
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                            <AlertTriangle 
                                size={20} 
                                color={theme.palette.warning.main} 
                                style={{ marginRight: theme.spacing(1.5), marginTop: '2px' }} 
                            />
                            <Typography variant="subtitle1" fontWeight={600} color="warning.main">
                                {t('chatbot.validation.inactiveTrigger.title')}
                            </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ ml: 4 }}>
                            {t('chatbot.validation.inactiveTrigger.description')}
                        </Typography>
                    </Paper>
                );

            case 'DISCONNECTED_NODE':
                return (
                    <Paper
                        key={`issue-${index}`}
                        elevation={0}
                        sx={{
                            mb: 3,
                            p: 2.5,
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                            backgroundColor: alpha(theme.palette.warning.main, 0.05)
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                            <AlertTriangle
                                size={20}
                                color={theme.palette.warning.main}
                                style={{ marginRight: theme.spacing(1.5), marginTop: '2px' }}
                            />
                            <Typography variant="subtitle1" fontWeight={600} color="warning.main">
                                {t('chatbot.validation.disconnectedNode.title')}
                            </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ ml: 4, mb: 1.5 }}>
                            {t('chatbot.validation.disconnectedNode.description')}
                        </Typography>
                        <Box
                            sx={{
                                ml: 4,
                                mb: 2,
                                p: 1.5,
                                borderRadius: 1.5,
                                backgroundColor: alpha(theme.palette.background.paper, 0.7),
                                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                maxHeight: '120px',
                                overflowY: 'auto',
                                '&::-webkit-scrollbar': {
                                    width: '8px',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: alpha(theme.palette.text.secondary, 0.2),
                                    borderRadius: '4px',
                                },
                                '&::-webkit-scrollbar-track': {
                                    backgroundColor: 'transparent',
                                }
                            }}
                        >
                            {validationResult.disconnectedNodes?.map(node => (
                                <Typography key={node.id} variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
                                    • {node.type === 'sendMessage' ? t('chatbot.nodeTypes.sendMessage.shortTitle') :
                                       node.type === 'question' ? t('chatbot.nodeTypes.question.shortTitle') :
                                       node.type === 'messageContact' ? t('chatbot.nodeTypes.messageContact.shortTitle') :
                                       node.type === 'agent' ? t('chatbot.nodeTypes.agent.shortTitle') : node.type}
                                    {node.label ? ` (${node.label})` : ` (${node.id})`}
                                </Typography>
                            ))}
                        </Box>
                    </Paper>
                );

            case 'OVERLAP':
                return (
                    <Paper
                        key={`issue-${index}`}
                        elevation={0}
                        sx={{
                            mb: 3,
                            p: 2.5,
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                            backgroundColor: alpha(theme.palette.warning.main, 0.05)
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                            <AlertTriangle
                                size={20}
                                color={theme.palette.warning.main}
                                style={{ marginRight: theme.spacing(1.5), marginTop: '2px' }}
                            />
                            <Typography variant="subtitle1" fontWeight={600} color="warning.main">
                                {t('chatbot.validation.overlap.title')}
                            </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ ml: 4, mb: 1.5 }}>
                            {t('chatbot.validation.overlap.description')}
                        </Typography>
                        <Box 
                            sx={{ 
                                ml: 4, 
                                mb: 2, 
                                p: 1.5, 
                                borderRadius: 1.5, 
                                backgroundColor: alpha(theme.palette.background.paper, 0.7),
                                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                maxHeight: '120px', 
                                overflowY: 'auto',
                                '&::-webkit-scrollbar': {
                                    width: '8px',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: alpha(theme.palette.text.secondary, 0.2),
                                    borderRadius: '4px',
                                },
                                '&::-webkit-scrollbar-track': {
                                    backgroundColor: 'transparent',
                                }
                            }}
                        >
                            {validationResult.overlappingBots?.map(bot => (
                                <Typography key={bot._id} variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
                                    • {bot.name}
                                </Typography>
                            ))}
                        </Box>
                        <Typography variant="body1" sx={{ ml: 4 }}>
                            {t('chatbot.validation.overlap.footer')}
                        </Typography>
                    </Paper>
                );

            case 'EMPTY_MESSAGES':
                return (
                    <Paper 
                        key={`issue-${index}`} 
                        elevation={0} 
                        sx={{ 
                            mb: 3, 
                            p: 2.5, 
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                            backgroundColor: alpha(theme.palette.warning.main, 0.05)
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                            <AlertTriangle 
                                size={20} 
                                color={theme.palette.warning.main} 
                                style={{ marginRight: theme.spacing(1.5), marginTop: '2px' }} 
                            />
                            <Typography variant="subtitle1" fontWeight={600} color="warning.main">
                                {t('chatbot.validation.emptyMessages.title')}
                            </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ ml: 4, mb: 1.5 }}>
                            {t('chatbot.validation.emptyMessages.description')}
                        </Typography>
                        <Box 
                            sx={{ 
                                ml: 4, 
                                mb: 2, 
                                p: 1.5, 
                                borderRadius: 1.5, 
                                backgroundColor: alpha(theme.palette.background.paper, 0.7),
                                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                maxHeight: '120px', 
                                overflowY: 'auto',
                                '&::-webkit-scrollbar': {
                                    width: '8px',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: alpha(theme.palette.text.secondary, 0.2),
                                    borderRadius: '4px',
                                },
                                '&::-webkit-scrollbar-track': {
                                    backgroundColor: 'transparent',
                                }
                            }}
                        >
                            {validationResult.emptyMessageNodes?.map(node => (
                                <Typography key={node.id} variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
                                    • {node.type === 'sendMessage' ? t('chatbot.nodeTypes.sendMessage.shortTitle') :
                                       node.type === 'question' ? t('chatbot.nodeTypes.question.shortTitle') :
                                       node.type === 'messageContact' ? t('chatbot.nodeTypes.messageContact.shortTitle') : node.type}
                                    {node.label ? ` (${node.label})` : ` (${node.id})`}
                                </Typography>
                            ))}
                        </Box>
                    </Paper>
                );

            default:
                return null;
        }
    };

    const renderWarningContent = () => {
        // If there are no issues, show a simple confirmation
        if (!validationResult.issues || validationResult.issues.length === 0) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                    <Info size={24} color={theme.palette.info.main} style={{ marginRight: theme.spacing(2) }} />
                    <Typography variant="body1">
                        {t('chatbot.validation.confirmAction', { action })}
                    </Typography>
                </Box>
            );
        }

        // If there's only one issue, render it
        if (validationResult.issues.length === 1) {
            return renderIssueContent(validationResult.issues[0].type, 0);
        }

        // If there are multiple issues, render them all with dividers
        return (
            <>
                <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{ 
                        fontWeight: 600, 
                        mb: 3, 
                        display: 'flex', 
                        alignItems: 'center',
                        color: theme.palette.text.primary
                    }}
                >
                    <AlertTriangle 
                        size={20} 
                        style={{ marginRight: theme.spacing(1.5) }} 
                        color={theme.palette.warning.main}
                    />
                    {t('chatbot.validation.multipleIssuesDetected')}
                </Typography>
                {validationResult.issues.map((issue, index) => (
                    <React.Fragment key={`issue-wrapper-${index}`}>
                        {renderIssueContent(issue.type, index)}
                        {index < (validationResult.issues?.length ?? 0) - 1 && (
                            <Divider sx={{ my: 2.5, opacity: 0.6 }} />
                        )}
                    </React.Fragment>
                ))}
                <Paper 
                    elevation={0} 
                    sx={{ 
                        mt: 3, 
                        p: 2, 
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.info.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                    }}
                >
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                        <Info 
                            size={18} 
                            color={theme.palette.info.main} 
                            style={{ marginRight: theme.spacing(1.5), flexShrink: 0 }} 
                        />
                        {t('chatbot.validation.recommendedBeforeProceed', {
                            action: isDeployment ? t('chatbot.actionDeploying') : t('chatbot.actionActivating')
                        })}
                    </Typography>
                </Paper>
            </>
        );
    };

    // Show confirm button for all cases except when the only issue is EMPTY_FLOW
    const showConfirmButton = !(
        validationResult.issues?.length === 1 && 
        validationResult.issues[0].type === 'EMPTY_FLOW'
    );

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            PaperProps={{
                elevation: 5,
                sx: {
                    borderRadius: 2,
                    overflow: 'hidden',
                    width: '550px',
                    maxWidth: '90vw'
                }
            }}
        >
            <DialogTitle sx={{ 
                py: 2.5, 
                px: 3,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                backgroundColor: theme.palette.background.paper,
                fontWeight: 600,
                marginBottom: '20px'
            }}>
                {isDeployment ? t('chatbot.deployBot') : t('chatbot.activateBot')}
            </DialogTitle>
            <DialogContent sx={{ 
                p: 3, 
                maxHeight: '60vh', 
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                    width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: alpha(theme.palette.text.secondary, 0.2),
                    borderRadius: '4px',
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: 'transparent',
                }
            }}>
                {renderWarningContent()}
            </DialogContent>
            <DialogActions sx={{ 
                px: 3, 
                py: 2,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                backgroundColor: alpha(theme.palette.background.default, 0.5)
            }}>
                <Button 
                    onClick={onClose} 
                    color="inherit" 
                    disabled={loading}
                    sx={{ 
                        fontWeight: 500,
                        px: 2.5,
                        borderRadius: 1.5
                    }}
                >
                    {!showConfirmButton ? t('common.close') : t('common.cancel')}
                </Button>
                {showConfirmButton && (
                    <Button 
                        onClick={onConfirm} 
                        variant="contained" 
                        color="primary"
                        disabled={loading}
                        sx={{
                            minWidth: '100px',
                            height: '36px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 500,
                            px: 2.5,
                            borderRadius: 1.5,
                            boxShadow: 2
                        }}
                    >
                        {loading ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : buttonText}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}; 
